<template>
  <div v-if="hasAnyPermission('faq-view')" class="py-4 container-fluid">
    <ToastPrime position="bottom-left" />
    <div class="card border-0">
      <div
        class="card-header bg-white py-3 d-flex flex-wrap align-items-center justify-content-between gap-3 border-bottom">
        <div class="d-flex align-items-center gap-2">
          <h5 class="mb-0 fw-bold">FAQ List</h5>
        </div>
        <div class="d-flex flex-wrap gap-2 flex-grow-1 justify-content-center">
          <IconField class="search-field">
            <InputIcon>
              <i class="pi pi-search" />
            </InputIcon>
            <InputText
v-model="table.search" type="text" size="small" placeholder="Search FAQ Question or Answer..."
              class="rounded-pill" style="min-width: 500px;" @keyup="filter" />
          </IconField>
        </div>
        <div>
          <button v-if="hasAnyPermission('faq-create')" type="button" class="mx-1 mb-0 btn btn-sm btn-primary" @click="openAddModal"> Add FAQ
          </button>
        </div>
      </div>
      <div style="height: 500px; overflow-y: auto;">
        <DataTable
:value="table.onLoad ? dummyRows : table.data" removable-sort scrollable scroll-height="flex"
          table-style="min-width: 100%; width: max-content;">
          <Column field="question" header="Question" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <div class="value-table-wrapped" style="max-width: 300px;">
                {{ slotProps.data && slotProps.data.question ? slotProps.data.question : '' }}
              </div>
            </template>
          </Column>
          <Column field="answer" header="Answer" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <div class="line-clamp-2" style="max-width: 400px;">
                {{ stripHtml(slotProps.data && slotProps.data.answer ? slotProps.data.answer : '') }}
              </div>
            </template>
          </Column>
          <Column field="created_at" header="Created At" header-class="bg-light text-nowrap text-dark" sortable>
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              {{ slotProps.data && slotProps.data.created_at ? formatDate(slotProps.data.created_at) : '' }}
            </template>
          </Column>
          <Column header="Actions" header-class="bg-light">
            <template v-if="table.onLoad" #body>
              <Skeleton></Skeleton>
            </template>
            <template v-else #body="slotProps">
              <ButtonPrime
v-tooltip.bottom="'View Detail'" icon="pi pi-eye" outlined rounded size="small" class="me-2"
                @click="viewDetail(slotProps.data.id)" />
              <ButtonPrime
v-if="hasAnyPermission('faq-update')" v-tooltip.bottom="'Update'" icon="pi pi-pencil" outlined rounded size="small" class="me-2"
                @click="openEditModal(slotProps.data)" />
              <ButtonPrime
v-if="hasAnyPermission('faq-delete')" v-tooltip.bottom="'Delete'" icon="pi pi-trash" outlined rounded severity="danger"
                size="small" @click="deleteItem(slotProps.data.id)" />
            </template>
          </Column>
          <template #empty>
            <div style="text-align: center; padding: 40px 0;">
              <img src="/no_data.png" alt="No Data" style="max-width: 330px; opacity: 0.7; margin-bottom: 0;" />
              <div style="color: #888; font-size: 1.1em; font-weight: 500;">
                No Data
              </div>
            </div>
          </template>
        </DataTable>
      </div>
      <div class="card-footer pb-0 d-flex justify-content-between align-items-center">
        <BasePagination
v-model="table.page.current_pages" :page-count="table.page?.total_pages || 1" size="sm"
          @click="changePage()" />
        <p>
          {{ (table.page.current_pages - 1) * table.page.per_pages + table.data.length }} of {{ table.total }}
        </p>
      </div>
    </div>
    <DialogPrime v-model:visible="modal.visible" modal style="width: 30rem; margin: 10px;" class="faq-modal">
      <template #header>
        <div class="d-flex align-items-center gap-3">
          <h5 class="mb-0 fw-bold text-dark">{{ modal.title }}</h5>
        </div>
      </template>
      <div class="row g-4">
        <!-- Section: Content -->
        <div class="col-12">
          <div class="row g-3">
            <div class="col-12">
              <label for="question" class="form-label fw-semibold">Question <span class="text-danger">*</span></label>
              <InputText
id="question" v-model="modal.data.question"
                placeholder="e.g. How to track my order?" :invalid="!!modal.errorMessage['question']" fluid />
            </div>
            <div class="col-12">
              <label for="answer" class="form-label fw-semibold">Detailed Answer <span
                  class="text-danger">*</span></label>
              <div id="editor" ref="editor" style="height: 300px;"></div>
              <small v-if="modal.errorMessage['answer']" class="text-danger">{{ modal.errorMessage['answer'] }}</small>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <div class="d-flex justify-content-end gap-2 pt-3 border-top w-100 mt-2 px-1">
          <ButtonPrime
type="button" label="Cancel" severity="danger" raised size="small" class="px-4 border-0"
            @click="closeModal()" />
          <ButtonPrime
v-if="canSaveFaq" type="button" severity="contrast" raised size="small" :disabled="modal.onLoading" label="Save FAQ"
            class="px-4" @click="save()">
            <template v-if="modal.onLoading" #icon>
              <i class="fas fa-spinner fa-spin me-2"></i>
            </template>
          </ButtonPrime>
        </div>
      </template>
    </DialogPrime>
  </div>
  <NoAccess v-else />
</template>

<script>
import { mapState } from "vuex";
import BasePagination from "@/components/BasePagination.vue";
import moment from "moment";
import api from "@/helpers/api";
import faqService from "@/services/faqService";
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import NoAccess from "@/components/NoAccess.vue";

export default {
  components: {
    BasePagination,
    NoAccess,
  },
  computed: {
    ...mapState({
      authUserPermissions: (state) => state.auth.userHasPermissions || [],
    }),
    canSaveFaq() {
      return this.modal.action === 'update'
        ? this.hasAnyPermission('faq-update')
        : this.hasAnyPermission('faq-create');
    },
  },
  data() {
    return {
      table: {
        onLoad: true,
        search: this.$route.query.search || "",
        total: 0,
        data: [],
        limit: 100,
        page: {
          current_pages: 1,
          per_pages: 5,
          total_pages: 1,
        },
        loadTimeout: null,
      },
      dummyRows: Array.from({ length: 10 }, () => ({})),
      modal: {
        visible: false,
        data: { question: '', answer: '' },
        errorMessage: {},
        onLoading: false,
        title: '',
        action: '', // 'create' atau 'update'
        editDataId: null, // digunakan untuk update
      },
      quillEditor: null,
    };
  },
  watch: {
    'modal.data.question'() {
      if (this.modal.errorMessage['question']) {
        delete this.modal.errorMessage['question'];
      }
    },
    'modal.visible'(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initQuillEditor();
        });
      } else {
        this.destroyQuillEditor();
      }
    }
  },
  mounted() {
    if (this.hasAnyPermission('faq-view')) {
      this.get();
    }
  },
  methods: {
    hasAnyPermission(...perms) {
      return perms.some((p) => this.authUserPermissions.includes(p));
    },
    initQuillEditor() {
      if (this.quillEditor) {
        this.destroyQuillEditor();
      }
      
      const editorElement = this.$refs.editor;
      if (!editorElement) return;

      this.quillEditor = new Quill(editorElement, {
        theme: 'snow',
        placeholder: 'Provide a clear and concise answer...',
        modules: {
          toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
          ]
        }
      });

      // Set initial content
      if (this.modal.data.answer) {
        this.quillEditor.root.innerHTML = this.modal.data.answer;
      }

      // Listen to changes
      this.quillEditor.on('text-change', () => {
        this.modal.data.answer = this.quillEditor.root.innerHTML;
        if (this.modal.errorMessage['answer']) {
          delete this.modal.errorMessage['answer'];
        }
      });
    },
    destroyQuillEditor() {
      if (this.quillEditor) {
        this.quillEditor = null;
      }
    },
    async get() {
      this.table.onLoad = true;
      try {
        const response = await api(
          this,
          faqService.list({
            search: this.table.search,
            limit: this.table.limit,
            page: this.table.page.current_pages,
          }),
          false
        );
        const paged = response.data || {};
        this.table.data = paged.data || [];
        this.table.total = paged.total || this.table.data.length;
        this.table.page.total_pages = paged.last_page || 1;
        this.table.page.current_pages = paged.current_page || 1;
        this.table.page.per_pages = paged.per_page || this.table.limit;
      } catch (error) {
        this.table.data = [];
        this.table.total = 0;
        this.table.page.total_pages = 1;
      } finally {
        this.table.onLoad = false;
      }
    },
    filter() {
      clearTimeout(this.table.loadTimeout);
      this.table.loadTimeout = setTimeout(() => {
        this.$router.push({ query: this.table.search ? { search: this.table.search } : null });
        this.table.page.current_pages = 1;
        this.get();
      }, 500);
    },
    changePage() {
      this.get();
    },
    formatDate(date) {
      return date ? moment(date).format("DD MMMM YYYY, HH:mm") : "-";
    },
    stripHtml(html) {
      const tmp = document.createElement("DIV");
      tmp.innerHTML = html;
      return tmp.textContent || tmp.innerText || "";
    },
    openAddModal() {
      this.modal.visible = true;
      this.modal.data = { question: '', answer: '' };
      this.modal.errorMessage = {};
      this.modal.title = 'Add FAQ';
      this.modal.action = 'create';
      this.modal.editDataId = null;
      this.modal.onLoading = false;
    },
    openEditModal(data) {
      this.modal.visible = true;
      this.modal.data = {
        question: data.question || '',
        answer: data.answer || ''
      };
      this.modal.errorMessage = {};
      this.modal.title = 'Edit FAQ';
      this.modal.action = 'update';
      this.modal.editDataId = data.id;
      this.modal.onLoading = false;
    },
    async save() {
      this.modal.errorMessage = {};
      let hasError = false;

      if (!this.modal.data.question || this.modal.data.question.trim() === '') {
        this.modal.errorMessage['question'] = 'Question is required';
        hasError = true;
      }

      // Get content from Quill editor
      const answerContent = this.quillEditor ? this.quillEditor.root.innerHTML : this.modal.data.answer;
      const answerText = this.quillEditor ? this.quillEditor.getText().trim() : this.modal.data.answer.trim();
      
      if (!answerText || answerText === '') {
        this.modal.errorMessage['answer'] = 'Answer is required';
        hasError = true;
      }

      if (hasError) return;

      this.modal.onLoading = true;
      try {
        const payload = {
          question: this.modal.data.question.trim(),
          answer: answerContent
        };

        if (this.modal.action === 'update' && this.modal.editDataId) {
          await api(this, faqService.patch(this.modal.editDataId, payload), true);
        } else {
          await api(this, faqService.create(payload), true);
        }

        this.modal.visible = false;
        this.modal.action = '';
        this.modal.editDataId = null;
        this.get(); // refresh table
      } catch (error) {
        if (error.response?.data?.code == 400 && error.response.data.data) {
          error.response.data.data.forEach((v) => {
            this.modal.errorMessage[v.path] = v.msg;
          });
        } else {
          console.error('Error saving FAQ:', error);
        }
      } finally {
        this.modal.onLoading = false;
      }
    },
    closeModal() {
      this.modal.visible = false;
      this.modal.data = { question: '', answer: '' };
      this.modal.errorMessage = {};
      this.modal.action = '';
      this.modal.editDataId = null;
      this.modal.onLoading = false;
    },
    viewDetail(id) {
      this.$router.push({ name: 'FAQ Detail', params: { moduleSlug: 'material-management', id } });
    },
    deleteItem(id) {
      this.confirmDialog("You won't be able to revert this!", "delete it").then((result) => {
        if (result.isConfirmed) {
          api(this, faqService.delete(id)).then(() => {
            this.get();
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.value-table-wrapped {
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-wrap: break-word;
}
</style>
